// Pure edit helpers behind the Integration page's visual Mapping editor
// (roadmap #9 Phase 1, slice 6). Every function returns a NEW spec merged
// over the original, so keys the editor doesn't model (provider, channel,
// target, intervalSec, ...) survive an edit untouched, and an unedited spec
// is returned byte-for-byte identical. Nothing here evaluates a mapping -
// mappingEngine.ts/mappingPreview.ts remain the only place behavior lives.
import { resolvePath, type JsonValue, type MappingRule, type MappingSpec, type TransformSpec } from './mappingEngine';
import type { PreviewRecordResult } from './mappingPreview';

const RULE_KEYS = new Set(['to', 'from', 'const', 'default', 'cast', 'unitScale', 'timestamp', 'map', 'required', 'ops']);
const OP_KEYS = new Set(['op', 'value', 'from']);

// An `ops` chain the editor can show and edit without losing anything: a list
// of {op, value?, from?} (see mappingEngine.ts applyOps).
function opsEditable(ops: unknown): boolean {
  if (!Array.isArray(ops)) return false;
  return ops.every(
    (o) =>
      isPlainObject(o) &&
      Object.keys(o).every((k) => OP_KEYS.has(k)) &&
      typeof o.op === 'string' &&
      (o.value === undefined || typeof o.value === 'number') &&
      (o.from === undefined || typeof o.from === 'string'),
  );
}

const isPlainObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const isScalar = (v: unknown) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

function ruleEditable(rule: unknown): boolean {
  if (!isPlainObject(rule)) return false;
  for (const k of Object.keys(rule)) if (!RULE_KEYS.has(k)) return false;
  if (rule.to !== undefined && typeof rule.to !== 'string') return false;
  if (rule.from !== undefined && typeof rule.from !== 'string') return false;
  if (rule.cast !== undefined && typeof rule.cast !== 'string') return false;
  if (rule.timestamp !== undefined && typeof rule.timestamp !== 'string') return false;
  if (rule.unitScale !== undefined && typeof rule.unitScale !== 'number') return false;
  if (rule.required !== undefined && typeof rule.required !== 'boolean') return false;
  if (rule.ops !== undefined && !opsEditable(rule.ops)) return false;
  if (rule.const !== undefined && !isScalar(rule.const)) return false;
  if (rule.default !== undefined && !isScalar(rule.default)) return false;
  if (rule.map !== undefined) {
    if (!isPlainObject(rule.map)) return false;
    if (!Object.values(rule.map).every(isScalar)) return false;
  }
  return true;
}

// True only when the visual editor can model every rule in the spec. Anything
// else (unknown rule keys, non-object outputs, structured const/default...)
// makes the editor read-only and points the user at the raw JSON, so it can
// never silently drop something it doesn't understand.
// Keys an output may carry beyond its rules: the list it reads from the document
// (one document, many metrics), how many newest records to keep, its calc block.
const OUTPUT_KEYS = ['rules', 'recordPath', 'last', 'calc'];

export function isEditableSpec(spec: unknown): boolean {
  if (!isPlainObject(spec)) return false;
  if (spec.outputs !== undefined) {
    if (!Array.isArray(spec.outputs) || spec.outputs.length === 0) return false;
    for (const o of spec.outputs) {
      if (!isPlainObject(o)) return false;
      if (Object.keys(o).some((k) => !OUTPUT_KEYS.includes(k))) return false;
      if (o.recordPath !== undefined && typeof o.recordPath !== 'string') return false;
      if (o.last !== undefined && typeof o.last !== 'number') return false;
      if (o.calc !== undefined && !isPlainObject(o.calc)) return false;
      if (o.rules !== undefined && (!Array.isArray(o.rules) || !o.rules.every(ruleEditable))) return false;
    }
    return true;
  }
  if (spec.rules !== undefined) return Array.isArray(spec.rules) && spec.rules.every(ruleEditable);
  return true;
}

export function getOutputs(spec: MappingSpec): MappingRule[][] {
  if (Array.isArray(spec.outputs)) return spec.outputs.map((o) => (Array.isArray(o?.rules) ? o.rules : []));
  return [Array.isArray(spec.rules) ? spec.rules : []];
}

export function setOutputRules(spec: MappingSpec, outputIndex: number, rules: MappingRule[]): MappingSpec {
  if (Array.isArray(spec.outputs)) {
    return { ...spec, outputs: spec.outputs.map((o, i) => (i === outputIndex ? { ...o, rules } : o)) };
  }
  return { ...spec, rules };
}

// Single implicit output (spec.rules) becomes an explicit outputs[] the first
// time a second one is added - engine behavior is identical (it ignores
// spec.rules once outputs exists, so that key is moved, not duplicated).
export function addOutput(spec: MappingSpec): MappingSpec {
  if (Array.isArray(spec.outputs)) return { ...spec, outputs: [...spec.outputs, { rules: [] }] };
  const { rules, ...rest } = spec;
  return { ...rest, outputs: [{ rules: Array.isArray(rules) ? rules : [] }, { rules: [] }] };
}

// Points one output at its own list of records (undefined = the mapping's recordPath).
export function setOutputRecordPath(spec: MappingSpec, outputIndex: number, path: string | undefined): MappingSpec {
  if (!Array.isArray(spec.outputs)) return spec;
  return {
    ...spec,
    outputs: spec.outputs.map((o, i) => {
      if (i !== outputIndex) return o;
      const { recordPath: _drop, ...rest } = o;
      void _drop;
      return path ? { ...rest, recordPath: path } : rest;
    }),
  };
}

export function removeOutput(spec: MappingSpec, outputIndex: number): MappingSpec {
  if (!Array.isArray(spec.outputs) || spec.outputs.length <= 1) return spec;
  return { ...spec, outputs: spec.outputs.filter((_, i) => i !== outputIndex) };
}

// Patch one rule; an `undefined` patch value deletes that key so an emptied
// input doesn't leave `"cast": ""` behind.
export function patchRule(rule: MappingRule, patch: Partial<Record<keyof MappingRule, unknown>>): MappingRule {
  const next: Record<string, unknown> = { ...rule };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete next[k];
    else next[k] = v;
  }
  return next as MappingRule;
}

export function moveRule(rules: MappingRule[], from: number, to: number): MappingRule[] {
  if (to < 0 || to >= rules.length || from === to) return rules;
  const next = [...rules];
  const [r] = next.splice(from, 1);
  next.splice(to, 0, r);
  return next;
}

// Writes spec.transform, dropping empty parts (and the whole key when nothing
// is left) so an untouched editor never adds a stray `"transform": {}`.
export function setTransform(spec: MappingSpec, transform: TransformSpec): MappingSpec {
  const t: TransformSpec = {};
  if (transform.groupBy?.length) t.groupBy = transform.groupBy;
  const aggs = (transform.aggregate ?? []).filter((a) => a.op === 'count' || !!a.field);
  if (aggs.length) t.aggregate = aggs;
  const sorts = (transform.sortBy ?? []).filter((k) => (typeof k === 'string' ? k : k.field));
  if (sorts.length) t.sortBy = sorts;
  if (typeof transform.limit === 'number' && Number.isFinite(transform.limit) && transform.limit >= 0) t.limit = Math.floor(transform.limit);
  if (transform.select?.length) t.select = transform.select;
  const { transform: _drop, ...rest } = spec;
  return Object.keys(t).length ? { ...rest, transform: t } : rest;
}

export function setStaticLabels(spec: MappingSpec, labels: Record<string, string>): MappingSpec {
  const { staticLabels: _drop, ...rest } = spec;
  return Object.keys(labels).length ? { ...rest, staticLabels: labels } : rest;
}

// `key=value` lines <-> the rule.map object. Blank/keyless lines are dropped.
export function mapToLines(map: Record<string, JsonValue> | undefined): string {
  return map ? Object.entries(map).map(([k, v]) => `${k}=${v === null ? '' : String(v)}`).join('\n') : '';
}

export function linesToMap(text: string): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const i = line.indexOf('=');
    if (i <= 0) continue;
    out[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  }
  return Object.keys(out).length ? out : undefined;
}

export interface SourcePath {
  path: string;
  sample: string;
}

// Leaf dot-paths of a Record (arrays by index), the vocabulary the engine's
// `from` understands. Capped so a huge payload can't freeze the tree.
export function flattenPaths(record: JsonValue | undefined, max = 300): SourcePath[] {
  const out: SourcePath[] = [];
  const walk = (v: JsonValue | undefined, prefix: string) => {
    if (out.length >= max || v === undefined) return;
    if (Array.isArray(v)) v.forEach((c, i) => walk(c, prefix ? `${prefix}.${i}` : String(i)));
    else if (v !== null && typeof v === 'object') for (const [k, c] of Object.entries(v)) walk(c, prefix ? `${prefix}.${k}` : k);
    else if (prefix) out.push({ path: prefix, sample: v === null ? 'null' : String(v) });
  };
  walk(record, '');
  return out;
}

export interface PreviewSummary {
  records: number;
  produced: number;
  rejected: number;
  // Count of `to: "signalRef"` outputs this preview can't evaluate itself
  // (see isPendingSignalRef) - real, will-be-emitted Signals, just not
  // verifiable client-side. Kept separate from both produced (this preview
  // never actually confirmed one) and rejected (nothing is wrong).
  pendingSignalRef: number;
  failures: string[];
}

function scalarResolves(v: JsonValue | undefined): boolean {
  return v !== undefined && v !== null && !Array.isArray(v) && typeof v !== 'object';
}

function reasonFor(status: string, rules: MappingRule[], record: JsonValue): string {
  if (status === 'dropped-required') {
    const missing = rules
      .filter((r) => r.required && r.to)
      .filter((r) => !(scalarResolves(resolvePath(record, r.from)) && r.from) && !scalarResolves(r.const) && !scalarResolves(r.default))
      .map((r) => `${r.to} (${r.from ? `path ${r.from}` : 'no source'})`);
    return `required field missing: ${missing.join(', ') || 'unknown'}`;
  }
  if (status === 'no-name') return 'no name - add a "name" rule';
  if (status === 'no-rules') return 'no rules configured';
  if (status === 'filtered-allowlist') return 'name not in metricAllowlist';
  if (status === 'skipped-non-finite') return 'value is not a finite number';
  return status;
}

// `unresolved-signal-ref` (docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md
// Phase 2/3: a `to: "signalRef"` output, e.g. from the "channel signal"
// picker) is never a rejection - the preview genuinely cannot resolve it
// (no server/Redis access, see mappingEngine.ts's OutputOutcome.signalRef
// comment), but the real ingest path will. Counting it as `rejected` (this
// function's caller used to, before this distinction existed) told a user
// picking an existing channel signal that their mapping was broken when it
// wasn't.
function isPendingSignalRef(status: string): boolean {
  return status === 'unresolved-signal-ref';
}

// "100 tested / 98 produced / 2 rejected" plus exact reasons per record
// (brief §7.7). Reasons are derived from the engine's own per-output status;
// only "which required field" is recomputed, from the same rule set.
export function summarizePreview(spec: MappingSpec, results: PreviewRecordResult[], maxFailures = 20): PreviewSummary {
  const outputs = getOutputs(spec);
  let produced = 0;
  let pendingSignalRef = 0;
  const failures: string[] = [];
  let rejected = 0;
  for (const r of results) {
    for (const o of r.outputs) {
      if (o.status === 'emitted') {
        produced++;
        continue;
      }
      if (isPendingSignalRef(o.status)) {
        pendingSignalRef++;
        continue;
      }
      rejected++;
      if (failures.length < maxFailures) {
        const prefix = outputs.length > 1 ? `Record ${r.recordIndex + 1}, output ${o.outputIndex + 1}` : `Record ${r.recordIndex + 1}`;
        failures.push(`${prefix}: ${reasonFor(o.status, outputs[o.outputIndex] ?? [], r.record)}`);
      }
    }
  }
  return { records: results.length, produced, rejected, pendingSignalRef, failures };
}
